import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import useQuery from 'hooks/apollo/useQuery';
import { LEASING_INCOME_SCHEMA_DETAILS } from 'features/leasingIncomeSchema/queries';
import LeasingIncomeSchemaEventsComponent from './LeasingIncomeSchemaEventsComponent';
import WrappedFakeText from 'components/common/WrappedFakeText';
import FlexBox from 'components/common/FlexBox';
import LeasingIncomeSchemaEventDetailsComponent from './LeasingIncomeSchemaEventDetailsComponent';
import { useMutation } from 'hooks/apollo/useMutation';
import { SAVE_LEASING_INCOME_SCHEMA_DETAILS } from 'features/leasingIncomeSchema/mutations';
import { LEASING_INCOME_SCHEMA_DETAILS_SAVED } from 'features/leasingIncomeSchema/subscriptions';
import useSubscription from 'hooks/apollo/useSubscription';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import { parseId } from 'utils/functions';
import { ServiceContext } from 'providers/ServiceProvider';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_LEASING_INCOME_SCHEMAS_WRITE } from 'constants/rightsCodes';
import { fromModel, greaterThan, lessThan, items, required, shape, stopOnFirstFailure, when } from '@totalsoft/pure-validations';
import { isPositiveOrZero, percentage, uniqueField } from 'utils/simple-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { negotiationLimitsInAbsoluteValuesVar, selectedSchemaDetailIdVar, isIncomeSchemaEditableVar } from '../../variables';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';
import { useReactiveVar } from '@apollo/client';

const validator = items(
  fromModel(detail =>
    shape({
      incomeTypeId: required,
      currencyId: required,
      currencyPercent: percentage,
      currencyValue: isPositiveOrZero,
      minValue: [isPositiveOrZero, lessThan(detail.maxValue) |> when(Boolean(detail.maxValue))] |> stopOnFirstFailure,
      maxValue: [isPositiveOrZero, greaterThan(detail.minValue) |> when(Boolean(detail.minValue))] |> stopOnFirstFailure,
      negotiationLimits: items(
        fromModel(limit =>
          shape({
            orderNo: [required, greaterThan(0), uniqueField] |> stopOnFirstFailure,
            lowerPercentLimit: percentage,
            upperPercentLimit: percentage,
            lowerValueLimit:
              [isPositiveOrZero, lessThan(limit.upperValueLimit) |> when(Boolean(limit.upperValueLimit))] |> stopOnFirstFailure,
            upperValueLimit:
              [isPositiveOrZero, greaterThan(limit.lowerValueLimit) |> when(Boolean(limit.lowerValueLimit))] |> stopOnFirstFailure
          })
        )
      )
    })
  )
);

const LeasingIncomeSchemaEventsContainer = ({ id }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const [activeService] = useContext(ServiceContext);

  const canWrite = usePermission(ADMIN_LEASING_INCOME_SCHEMAS_WRITE);
  const isEditable = useReactiveVar(isIncomeSchemaEditableVar);
  const [pending, setPending] = useState(false);

  const limitsInAbsoluteValues = useReactiveVar(negotiationLimitsInAbsoluteValuesVar);
  const [limitsInAbsoluteValuesDirty, setLimitsInAbsoluteValuesDirty] = useState(false);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);
  const [validation, validate] = useDirtyFieldValidation(validator);

  const { loading } = useQuery(LEASING_INCOME_SCHEMA_DETAILS, {
    variables: { id },
    skip: !id,
    onCompleted: data => {
      resetLens(data?.leasingIncomeSchemaDetails || []);
    }
  });

  // #region Save

  const [saveLeasingIncomeSchemaDetails] = useMutation(SAVE_LEASING_INCOME_SCHEMA_DETAILS, {
    onError: () => {
      setPending(false);
    }
  });

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;

    const sanitizedData = data.map(detail => {
      const sanitizedDetail = { ...detail, id: parseId(detail.id) };

      delete sanitizedDetail.eventName;
      delete sanitizedDetail.incomeTypeCode;
      delete sanitizedDetail.incomeTypeName;
      delete sanitizedDetail.incomeTypeCurrencyName;
      delete sanitizedDetail.incomeTypeUseFormula;
      delete sanitizedDetail.currencyCode;
      delete sanitizedDetail.currencyName;
      delete sanitizedDetail.measuringUnitName;
      delete sanitizedDetail.negotiationMomentName;

      sanitizedDetail.negotiationLimits = sanitizedDetail.negotiationLimits.map(limit => {
        const sanitizedLimit = { ...limit, id: parseId(limit.id) };

        sanitizedLimit.schemaId = id;
        sanitizedLimit.schemaDetailId = sanitizedDetail.id;
        delete sanitizedLimit.detailId;

        return sanitizedLimit;
      });

      return sanitizedDetail;
    });

    setPending(true);
    saveLeasingIncomeSchemaDetails({
      variables: { schemaId: id, limitsInAbsoluteValues: limitsInAbsoluteValues || false, details: sanitizedData }
    });
  };

  useSubscription(LEASING_INCOME_SCHEMA_DETAILS_SAVED, {
    skip: !id,
    variables: { id },
    onData: ({ client, data }) => {
      const newDetails = data?.data?.leasingIncomeSchemaDetailsSaved?.details || [];
      resetLens(prev => {
        const selectedSchemaDetailId = selectedSchemaDetailIdVar();
        if (!newDetails.some(detail => detail.id === selectedSchemaDetailId)) {
          const prevIndex = prev.findIndex(detail => detail.id === selectedSchemaDetailId);
          selectedSchemaDetailIdVar(newDetails[prevIndex]?.id);
        }
        return newDetails;
      });
      setLimitsInAbsoluteValuesDirty(false);
      client.writeQuery({
        query: LEASING_INCOME_SCHEMA_DETAILS,
        context: { serviceName: activeService?.name },
        variables: { id },
        data: { leasingIncomeSchemaDetails: newDetails }
      });
      setPending(false);
      addToast(t('LeasingIncomeSchema.DetailsSaved'), 'success');
    }
  });

  // #endregion

  const isDirty = useMemo(() => limitsInAbsoluteValuesDirty || checkDirty(dirtyInfo), [limitsInAbsoluteValuesDirty, dirtyInfo]);

  // #region Side effects

  useEffect(
    () => () => {
      selectedSchemaDetailIdVar(null);
    },
    []
  );

  useGlobalIsDirty(isDirty);
  const disabled = useMemo(() => !canWrite || pending || !isEditable, [canWrite, pending, isEditable]);

  // #endregion

  return (
    <FlexBox direction='column' mt={2} gap={2}>
      {loading && <WrappedFakeText />}
      {!loading && (
        <LeasingIncomeSchemaEventsComponent
          schemaId={id}
          lens={lens}
          onSave={handleSave}
          validation={validation}
          disabled={disabled}
          isDirty={isDirty}
        />
      )}
      {!loading && (
        <LeasingIncomeSchemaEventDetailsComponent
          schemaId={id}
          lens={lens}
          validation={validation}
          disabled={disabled}
          setLimitsInAbsoluteValuesDirty={setLimitsInAbsoluteValuesDirty}
        />
      )}
    </FlexBox>
  );
};

LeasingIncomeSchemaEventsContainer.propTypes = {
  id: PropTypes.number
};

export default LeasingIncomeSchemaEventsContainer;
